// src/pages/admin/AdminClubDetail.jsx
//
// Founder-only detail/edit page at /admin/clubs/:clubId. Loads the club
// doc + last 10 adminAudit entries, lets Kurt edit the four commercial
// fields, and flows every save through the `setClubTier` callable with
// a confirm modal. Stale-version guard via SHA-256 hash (matches the
// server algorithm).
//
// See features/admin-club-provisioning/01-requirements.md + 03-architecture.md.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import toast from 'react-hot-toast';
import { db } from '@/firebase/firebase';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import TierBadge from '@/components/admin/TierBadge';
import ConfirmTierChangeModal from '@/components/admin/ConfirmTierChangeModal';
import ClubSiteAdminSection from '@/components/admin/ClubSiteAdminSection';

const TIER_OPTIONS = [
  { value: '',               label: '— (none)' },
  { value: 'free',           label: 'Free' },
  { value: 'team',           label: 'Team' },
  { value: 'club',           label: 'Club' },
  { value: 'growth',         label: 'Growth' },
  { value: 'elite',          label: 'Elite' },
  { value: 'enterprise',     label: 'Enterprise' },
  { value: 'founding_club',  label: 'Founding (legacy enum)' },
];

// ──────────────────────────────────────────────────────────────────────
// Hash function — MUST match `hashVersion` in
// functions/modules/adminClubs.js exactly. Both render trialEndsAt as
// an ISO string before serializing.
async function hashVersion(obj) {
  const ts = obj.trialEndsAt;
  let tsNorm = null;
  if (ts != null) {
    if (typeof ts === 'string') tsNorm = ts;
    else if (ts instanceof Date) tsNorm = ts.toISOString();
    else if (typeof ts.toDate === 'function') tsNorm = ts.toDate().toISOString();
  }
  const norm = {
    tier:               obj.tier ?? null,
    foundingClubNumber: obj.foundingClubNumber ?? null,
    trialEndsAt:        tsNorm,
    seatLimit:          obj.seatLimit ?? null,
  };
  const buf = new TextEncoder().encode(JSON.stringify(norm));
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// Convert a Firestore Timestamp / Date / null into the YYYY-MM-DD string
// that <input type="date"> expects.
function timestampToDateInput(ts) {
  if (!ts) return '';
  const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function isoFromDateInput(s) {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function formatAuditTimestamp(ts) {
  if (!ts) return '';
  const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year:   'numeric',
    month:  '2-digit',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

export default function AdminClubDetail() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { isFounder } = usePlatformClaims();

  const [club, setClub] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [auditError, setAuditError] = useState(null);

  // Enhancement sections — loaded in parallel after the club doc resolves.
  // null = not-loaded-yet; arrays/numbers = loaded.
  const [memberCount, setMemberCount]     = useState(null);
  const [memberRoles, setMemberRoles]     = useState({}); // { director: n, club_admin: n, ... }
  const [recentRegs, setRecentRegs]       = useState(null);
  const [recentBroadcasts, setRecentBroadcasts] = useState(null);
  const [enhanceErrors, setEnhanceErrors] = useState({}); // { section: msg }

  // Edit form state — uses string-y inputs for HTML compat, normalized
  // back to typed values at submit time.
  const [tier, setTier] = useState('');
  const [foundingClubNumber, setFoundingClubNumber] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [seatLimit, setSeatLimit] = useState('');

  // Confirm modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInFlight, setConfirmInFlight] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const blocked = !isFounder;

  async function loadEverything() {
    setLoading(true);
    setLoadError(null);
    setAuditError(null);
    try {
      const clubRef = doc(db, 'clubs', clubId);
      const clubSnap = await getDoc(clubRef);
      if (!clubSnap.exists()) {
        setLoadError('not-found');
        setLoading(false);
        return;
      }
      const cdata = { id: clubSnap.id, ...clubSnap.data() };
      setClub(cdata);

      // Seed form state from loaded values.
      setTier(cdata.tier || '');
      setFoundingClubNumber(
        cdata.foundingClubNumber == null ? '' : String(cdata.foundingClubNumber)
      );
      setTrialEndsAt(timestampToDateInput(cdata.trialEndsAt));
      setSeatLimit(cdata.seatLimit == null ? '' : String(cdata.seatLimit));

      // Audit history — composite index on (targetType, targetId, createdAt desc).
      try {
        const aq = query(
          collection(db, 'adminAudit'),
          where('targetType', '==', 'club'),
          where('targetId',   '==', clubId),
          orderBy('createdAt', 'desc'),
          limit(10),
        );
        const aSnap = await getDocs(aq);
        setAudits(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (ae) {
        setAuditError(ae?.message || 'Failed to load audit history.');
      }

      // ── Enhancement sections (parallel, each failure isolated) ─────
      const errs = {};
      await Promise.all([
        // Members: count + role breakdown from clubs/{id}/members subcollection
        (async () => {
          try {
            const snap = await getDocs(collection(db, 'clubs', clubId, 'members'));
            setMemberCount(snap.size);
            const roles = {};
            snap.docs.forEach((d) => {
              const r = String(d.data()?.role || '').toLowerCase() || 'unknown';
              roles[r] = (roles[r] || 0) + 1;
            });
            setMemberRoles(roles);
          } catch (e) {
            errs.members = e?.message || 'Failed to load members.';
            setMemberCount(0);
          }
        })(),
        // Recent registrations
        (async () => {
          try {
            const q = query(
              collection(db, 'registrations'),
              where('clubId', '==', clubId),
              orderBy('createdAt', 'desc'),
              limit(10),
            );
            const snap = await getDocs(q);
            setRecentRegs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          } catch (e) {
            errs.registrations = e?.message || 'Failed to load registrations.';
            setRecentRegs([]);
          }
        })(),
        // Recent broadcasts
        (async () => {
          try {
            const q = query(
              collection(db, 'clubBroadcasts'),
              where('clubId', '==', clubId),
              orderBy('createdAt', 'desc'),
              limit(5),
            );
            const snap = await getDocs(q);
            setRecentBroadcasts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          } catch (e) {
            errs.broadcasts = e?.message || 'Failed to load broadcasts.';
            setRecentBroadcasts([]);
          }
        })(),
      ]);
      setEnhanceErrors(errs);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load club.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (blocked) return;
    if (!clubId) return;
    loadEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, blocked]);

  // Compute the deltas — only fields that differ from the loaded values.
  const deltas = useMemo(() => {
    if (!club) return [];
    const out = [];
    const loadedTier = club.tier || null;
    const proposedTier = tier === '' ? null : tier;
    if (proposedTier !== loadedTier) {
      out.push({ field: 'tier', before: loadedTier, after: proposedTier });
    }

    const loadedFCN = club.foundingClubNumber == null ? null : club.foundingClubNumber;
    const proposedFCN = foundingClubNumber === '' ? null : Number(foundingClubNumber);
    if (proposedFCN !== loadedFCN) {
      out.push({ field: 'foundingClubNumber', before: loadedFCN, after: proposedFCN });
    }

    const loadedTrial = timestampToDateInput(club.trialEndsAt);
    const proposedTrial = trialEndsAt || '';
    if (proposedTrial !== loadedTrial) {
      out.push({
        field:  'trialEndsAt',
        before: loadedTrial || null,
        after:  proposedTrial || null,
      });
    }

    const loadedSeats = club.seatLimit == null ? null : club.seatLimit;
    const proposedSeats = seatLimit === '' ? null : Number(seatLimit);
    if (proposedSeats !== loadedSeats) {
      out.push({ field: 'seatLimit', before: loadedSeats, after: proposedSeats });
    }
    return out;
  }, [club, tier, foundingClubNumber, trialEndsAt, seatLimit]);

  const dirty = deltas.length > 0;

  // Client-side validation (matches server, surfaced as a soft hint
  // before the modal opens — server still re-validates).
  const validationError = useMemo(() => {
    if (foundingClubNumber !== '') {
      const n = Number(foundingClubNumber);
      if (!Number.isInteger(n) || n < 1 || n > 5) {
        return 'Founding Club # must be an integer 1–5 or empty.';
      }
    }
    if (seatLimit !== '') {
      const s = Number(seatLimit);
      if (!Number.isInteger(s) || s < 0) {
        return 'Seat limit must be a non-negative integer or empty.';
      }
    }
    return null;
  }, [foundingClubNumber, seatLimit]);

  function resetForm() {
    if (!club) return;
    setTier(club.tier || '');
    setFoundingClubNumber(
      club.foundingClubNumber == null ? '' : String(club.foundingClubNumber),
    );
    setTrialEndsAt(timestampToDateInput(club.trialEndsAt));
    setSeatLimit(club.seatLimit == null ? '' : String(club.seatLimit));
  }

  function openConfirm() {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setConfirmError(null);
    setConfirmOpen(true);
  }

  async function handleConfirmSave(reason) {
    if (!club) return;
    setConfirmInFlight(true);
    setConfirmError(null);
    try {
      // Build the payload. ALWAYS send expectedVersion from the UI (S7).
      const loadedSnapshot = {
        tier:               club.tier ?? null,
        foundingClubNumber: club.foundingClubNumber ?? null,
        trialEndsAt:        club.trialEndsAt ?? null,
        seatLimit:          club.seatLimit ?? null,
      };
      const expectedVersion = await hashVersion(loadedSnapshot);

      const payload = {
        clubId,
        reason,
        expectedVersion,
      };
      for (const { field, after } of deltas) {
        if (field === 'trialEndsAt') {
          payload.trialEndsAt = after === null
            ? null
            : isoFromDateInput(after);
        } else {
          payload[field] = after;
        }
      }

      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'setClubTier');
      await fn(payload);

      toast.success('Saved.');
      setConfirmOpen(false);
      await loadEverything();
    } catch (e) {
      const msg =
        e?.message
        || e?.details
        || 'Save failed. Try again.';
      setConfirmError(msg);
    } finally {
      setConfirmInFlight(false);
    }
  }

  if (blocked) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">Insufficient privilege.</span>{' '}
            Founder claim required to view club provisioning.
          </p>
        </div>
      </div>
    );
  }

  if (loadError === 'not-found') {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Link to="/admin/clubs" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to clubs
        </Link>
        <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-12 text-center">
          <p className="text-zinc-600">This club doesn't exist.</p>
          <button
            type="button"
            onClick={() => navigate('/admin/clubs')}
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-lime-700 hover:underline"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to clubs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link to="/admin/clubs" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4">
        <ArrowLeftIcon className="h-4 w-4" />
        Back to clubs
      </Link>

      {loading && !club && (
        <div className="space-y-3">
          <div className="h-10 rounded-md bg-zinc-100 animate-pulse" />
          <div className="h-32 rounded-2xl bg-zinc-100 animate-pulse" />
          <div className="h-64 rounded-2xl bg-zinc-100 animate-pulse" />
        </div>
      )}

      {loadError && loadError !== 'not-found' && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Couldn't load this club</p>
            <p className="text-red-700">{loadError}</p>
          </div>
          <button
            type="button"
            onClick={loadEverything}
            className="px-2 py-1 rounded text-xs font-semibold border border-red-300 text-red-800 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {club && (
        <>
          {/* Summary header */}
          <div className="mb-4">
            <h1
              className="text-3xl font-bold uppercase tracking-tight text-zinc-900"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {club.name || <span className="text-zinc-400 italic">(unnamed)</span>}
            </h1>
            <div className="mt-1 flex items-center gap-3">
              <TierBadge tier={club.tier} foundingClubNumber={club.foundingClubNumber} />
              <span className="text-xs text-zinc-500">
                ID: <span className="font-mono">{club.id}</span>
              </span>
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-4 mb-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-2 text-zinc-700">
            <div>
              <span className="text-zinc-500">Owner:</span>{' '}
              <span className="font-mono text-xs">{club.ownerId || '—'}</span>
            </div>
            <div>
              <span className="text-zinc-500">Stripe Connected:</span>{' '}
              {club.stripeConnected ? 'Yes' : 'No'}
            </div>
            <div>
              <span className="text-zinc-500">Created:</span>{' '}
              {formatAuditTimestamp(club.createdAt) || '—'}
            </div>
            {club.lastTierUpdateAt && (
              <div>
                <span className="text-zinc-500">Last tier change:</span>{' '}
                {formatAuditTimestamp(club.lastTierUpdateAt)}
              </div>
            )}
          </div>

          {/* Club Website provisioning (founder only) */}
          <ClubSiteAdminSection clubId={clubId} />

          {/* Edit form */}
          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-5 mb-4">
            <h2
              className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4"
            >
              Commercial fields
            </h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="field-tier" className="block text-sm font-medium text-zinc-700">
                  Tier
                </label>
                <select
                  id="field-tier"
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="mt-1 block w-full sm:w-72 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
                >
                  {TIER_OPTIONS.map((opt) => (
                    <option key={opt.value || 'empty'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-zinc-500">
                  Founding clubs use <span className="font-semibold">growth</span> +
                  a Founding Club # below.
                </p>
              </div>

              <div>
                <label htmlFor="field-fcn" className="block text-sm font-medium text-zinc-700">
                  Founding Club #
                </label>
                <input
                  id="field-fcn"
                  type="number"
                  min={1}
                  max={5}
                  step={1}
                  value={foundingClubNumber}
                  onChange={(e) => setFoundingClubNumber(e.target.value)}
                  className="mt-1 block w-32 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
                />
                <p className="mt-1 text-xs text-zinc-500">
                  1–5. Blank = not a founding club.
                </p>
              </div>

              <div>
                <label htmlFor="field-trial" className="block text-sm font-medium text-zinc-700">
                  Trial ends at
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    id="field-trial"
                    type="date"
                    value={trialEndsAt}
                    onChange={(e) => setTrialEndsAt(e.target.value)}
                    className="rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
                  />
                  {trialEndsAt && (
                    <button
                      type="button"
                      onClick={() => setTrialEndsAt('')}
                      className="text-xs text-zinc-500 hover:text-zinc-800 underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="field-seats" className="block text-sm font-medium text-zinc-700">
                  Seat limit
                </label>
                <input
                  id="field-seats"
                  type="number"
                  min={0}
                  step={1}
                  value={seatLimit}
                  onChange={(e) => setSeatLimit(e.target.value)}
                  className="mt-1 block w-32 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
                />
                <p className="mt-1 text-xs text-zinc-500">Blank = unlimited.</p>
              </div>

              {validationError && (
                <p className="text-xs text-red-700 font-medium">{validationError}</p>
              )}

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={!dirty}
                  className="px-4 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Discard changes
                </button>
                <button
                  type="button"
                  onClick={openConfirm}
                  disabled={!dirty || !!validationError}
                  className="px-4 py-2 rounded-md text-sm font-semibold bg-lime-400 text-zinc-900 hover:bg-lime-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
            </div>
          </div>

          {/* Audit history */}
          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                Audit history (last 10)
              </h2>
              <button
                type="button"
                onClick={loadEverything}
                disabled={loading}
                className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900"
              >
                <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {auditError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-start gap-2 mb-3">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <span>{auditError}</span>
              </div>
            )}

            {!auditError && audits.length === 0 && (
              <p className="text-sm text-zinc-500">
                No history yet. The first edit you save will appear at the top.
              </p>
            )}

            {audits.length > 0 && (
              <ul className="space-y-3 text-sm">
                {audits.map((a) => (
                  <li key={a.id} className="border-l-2 border-lime-300 pl-3">
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <CheckCircleIcon className="h-3.5 w-3.5 text-lime-500" />
                      <span>{formatAuditTimestamp(a.createdAt)}</span>
                      <span className="text-zinc-400">·</span>
                      <span className="font-mono">{a.actorEmail || a.actorUid}</span>
                    </div>
                    <p className="mt-1 text-zinc-800">
                      {summarizeAudit(a)}
                    </p>
                    {a.reason && (
                      <p className="mt-1 text-xs text-zinc-500">
                        Reason: "{a.reason}"
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Members ────────────────────────────────────────────── */}
          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-5 mt-4">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
              Members {memberCount != null && <span className="text-zinc-400">({memberCount})</span>}
            </h2>
            {enhanceErrors.members && (
              <p className="text-xs text-red-700">Couldn't load members: {enhanceErrors.members}</p>
            )}
            {memberCount == null && !enhanceErrors.members && (
              <div className="h-8 w-32 rounded-md bg-zinc-100 animate-pulse" />
            )}
            {memberCount === 0 && !enhanceErrors.members && (
              <p className="text-sm text-zinc-500">No members yet.</p>
            )}
            {memberCount != null && memberCount > 0 && (
              <ul className="flex flex-wrap gap-2 text-xs">
                {Object.entries(memberRoles)
                  .sort((a, b) => b[1] - a[1])
                  .map(([role, n]) => (
                    <li key={role} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-100 text-zinc-700">
                      <span className="font-mono">{role}</span>
                      <span className="font-semibold tabular-nums">{n}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {/* ── Recent registrations ───────────────────────────────── */}
          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-5 mt-4">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
              Recent registrations {Array.isArray(recentRegs) && <span className="text-zinc-400">(last {recentRegs.length})</span>}
            </h2>
            {enhanceErrors.registrations && (
              <p className="text-xs text-red-700">Couldn't load: {enhanceErrors.registrations}</p>
            )}
            {recentRegs == null && !enhanceErrors.registrations && (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-12 rounded-md bg-zinc-100 animate-pulse" />
                ))}
              </div>
            )}
            {Array.isArray(recentRegs) && recentRegs.length === 0 && !enhanceErrors.registrations && (
              <p className="text-sm text-zinc-500">No registrations for this club yet.</p>
            )}
            {Array.isArray(recentRegs) && recentRegs.length > 0 && (
              <ul className="divide-y divide-zinc-100">
                {recentRegs.map((r) => (
                  <li key={r.id} className="py-2 text-sm">
                    <p className="font-semibold text-zinc-900">
                      {r.parentName || <span className="text-zinc-400 italic">(no parent name)</span>}
                    </p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      {r.parentEmail || '—'}
                      {' · '}
                      <span className="font-mono">{r.parentPhone || '—'}</span>
                      {r.language && (<>{' · '}{r.language}</>)}
                      {r.status && (<>{' · '}<span className="text-zinc-500">{r.status}</span></>)}
                    </p>
                    <p className="text-[10px] text-zinc-400 font-mono mt-0.5">
                      {formatAuditTimestamp(r.createdAt)} · {r.id}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Recent broadcasts ──────────────────────────────────── */}
          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-5 mt-4">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
              Recent broadcasts {Array.isArray(recentBroadcasts) && <span className="text-zinc-400">(last {recentBroadcasts.length})</span>}
            </h2>
            {enhanceErrors.broadcasts && (
              <p className="text-xs text-red-700">Couldn't load: {enhanceErrors.broadcasts}</p>
            )}
            {recentBroadcasts == null && !enhanceErrors.broadcasts && (
              <div className="space-y-2">
                {[0, 1].map((i) => (
                  <div key={i} className="h-12 rounded-md bg-zinc-100 animate-pulse" />
                ))}
              </div>
            )}
            {Array.isArray(recentBroadcasts) && recentBroadcasts.length === 0 && !enhanceErrors.broadcasts && (
              <p className="text-sm text-zinc-500">No club-level broadcasts sent yet.</p>
            )}
            {Array.isArray(recentBroadcasts) && recentBroadcasts.length > 0 && (
              <ul className="divide-y divide-zinc-100">
                {recentBroadcasts.map((b) => (
                  <li key={b.id} className="py-2 text-sm">
                    <p className="text-zinc-800">
                      {b.title || b.subject || <span className="text-zinc-400 italic">(no title)</span>}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {formatAuditTimestamp(b.createdAt)}
                      {b.audienceSize != null && (<>{' · '}{b.audienceSize} recipients</>)}
                      {b.channel && (<>{' · '}{b.channel}</>)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {confirmOpen && club && (
        <ConfirmTierChangeModal
          clubName={club.name || club.id}
          deltas={deltas}
          inFlight={confirmInFlight}
          errorMessage={confirmError}
          onCancel={() => {
            if (!confirmInFlight) setConfirmOpen(false);
          }}
          onConfirm={handleConfirmSave}
        />
      )}
    </div>
  );
}

// Build a human-readable summary of an audit entry from its before/after maps.
function summarizeAudit(a) {
  const before = a.before || {};
  const after  = a.after  || {};
  const fields = ['tier', 'foundingClubNumber', 'trialEndsAt', 'seatLimit'];
  const parts = [];
  for (const f of fields) {
    const b = normalizeForRender(before[f]);
    const af = normalizeForRender(after[f]);
    if (b !== af) parts.push(`${labelOf(f)}: ${b ?? '—'} → ${af ?? '—'}`);
  }
  if (parts.length === 0) return 'No-op write.';
  return parts.join(' · ');
}

function labelOf(f) {
  switch (f) {
    case 'tier':               return 'tier';
    case 'foundingClubNumber': return 'FC#';
    case 'trialEndsAt':        return 'trial';
    case 'seatLimit':          return 'seats';
    default: return f;
  }
}

function normalizeForRender(v) {
  if (v == null) return null;
  if (typeof v === 'object' && typeof v.toDate === 'function') {
    return v.toDate().toISOString().slice(0, 10);
  }
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return String(v);
}
